import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Box, Flex, Heading, Text, Skeleton, useMediaQuery } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ICareHome } from '../../interfaces';
import { useAuth } from '../../context/AuthContext.tsx';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';
import AleaFilterTags from '../../components/AleaFilterTags/AleaFilterTags.tsx';
import SearchBar from '../../components/SearchBar/SearchBar';
import AleaDownloadButton, { DownloadOption } from '../../components/AleaDownloadButton/AleaDownloadButton';
import { debounce } from 'lodash';
import { A4ReportDocument, type ReportColumn, type ReportRow } from '../../pdf/reports/A4ReportDocument';
import { downloadReactPdf, formatExportTimestamp, printReactPdf } from '../../utils/reactPdfExport';
import { SeniSmartLogo } from '../../pdf/logos/SeniSmartLogo';
import { buildCsv, downloadCsv, type CsvColumn } from '../../utils/csvExport';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';
import { useCareHomesListQuery } from './careHomeListPage.queries';

interface CareHomeTableItem {
  id: string;
  name: string;
  location: string;
  created_at: string;
  [key: string]: unknown;
}

const CareHomeListPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRestoring } = usePageFiltersStorage();
  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();
  const [isLargerThan1120] = useMediaQuery('(min-width: 1120px)');
  
  // Store current search input value that comes directly from SearchBar
  const inputValueRef = useRef<string>('');
  
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Extract parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const orderBy = queryParams.get('orderBy') || 'name';
  const sortOrder = queryParams.get('sortOrder') || 'asc';
  const urlPageParam = parseInt(queryParams.get('page') || '1', 10);
  const urlPerPageParam = queryParams.get('perPage');
  const urlQueryParam = queryParams.get('query') || '';
  const showArchived = queryParams.get('show_archived') === 'true';

  // State for search query - initialize from URL
  const [searchQuery, setSearchQuery] = useState(urlQueryParam);

  // State for current page - initialize from URL
  const [currentPage, setCurrentPage] = useState(urlPageParam);
  
  // State for perPage - If URL has perPage, use it, otherwise use stored value
  const [perPage, setPerPage] = useState(urlPerPageParam ? parseInt(urlPerPageParam, 10) : storedPerPage);

  // State for filter tag (Current/Archived) - initialize from URL
  const [selectedFilter, setSelectedFilter] = useState(showArchived ? 'archived' : 'current');

  useEffect(() => {
    if (isRestoring) return;

    const newParams = new URLSearchParams(location.search);
    let shouldUpdate = false;

    if (!newParams.get('orderBy')) {
      newParams.set('orderBy', 'name');
      shouldUpdate = true;
    }
    if (!newParams.get('sortOrder')) {
      newParams.set('sortOrder', 'asc');
      shouldUpdate = true;
    }
    if (!newParams.get('perPage') && storedPerPage !== 10) {
      newParams.set('perPage', storedPerPage.toString());
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    }
  }, [isRestoring, location.pathname, location.search, navigate, storedPerPage]);

  // Update states when URL changes
  useEffect(() => {
    const queryFromUrl = queryParams.get('query') || '';
    const pageFromUrl = parseInt(queryParams.get('page') || '1', 10);
    const perPageFromUrl = queryParams.get('perPage');
    const isArchived = queryParams.get('show_archived') === 'true';
    
    if (searchQuery !== queryFromUrl) {
      setSearchQuery(queryFromUrl);
    }
    
    if (currentPage !== pageFromUrl) {
      setCurrentPage(pageFromUrl);
    }
    
    if (perPageFromUrl) {
      const newPerPage = parseInt(perPageFromUrl, 10);
      if (perPage !== newPerPage) {
        setPerPage(newPerPage);
      }
    }

    if (selectedFilter !== (isArchived ? 'archived' : 'current')) {
      setSelectedFilter(isArchived ? 'archived' : 'current');
    }
  }, [location.search]);

  // Handle search input changes - this is called immediately when user types
  const handleSearch = (inputValue: string) => {
    // Store the latest input value in ref for debounce function to access
    inputValueRef.current = inputValue;
    
    // Show table skeleton loader immediately when user types (if value changed)
    // This provides immediate feedback in the table without showing general loader
    if (inputValue !== searchQuery) {
      setIsTableLoading(true);
    }
    
    // Update searchQuery state immediately to track current input
    setSearchQuery(inputValue);
    
    debouncedUpdateUrl();
  };

  // Debounced function to update URL with the latest input value
  const debouncedUpdateUrl = useCallback(
    debounce(() => {
      // Use the current value from ref to ensure we have the latest input
      const latestInput = inputValueRef.current;
      const newSearchParams = new URLSearchParams(location.search);
      
      if (latestInput) {
        newSearchParams.set('query', latestInput);
      } else {
        newSearchParams.delete('query');
      }
      
      // Reset to page 1 when searching
      newSearchParams.set('page', '1');
      
      // Update the URL - this will trigger the fetchCareHomes via useEffect
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    }, 1000),
    [location.pathname, location.search, navigate]
  );

  // Clear search handler
  const handleClearSearch = () => {
    // Clear the ref value
    inputValueRef.current = '';
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('query');
    // Reset to page 1 when clearing search
    newSearchParams.set('page', '1');
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  // Make sure to clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateUrl.cancel();
    };
  }, [debouncedUpdateUrl]);

  const careHomesQueryInput = useMemo(() => {
    const pageFromUrl = parseInt(queryParams.get('page') || '1', 10);
    const queryFromUrl = queryParams.get('query') || '';
    const isArchived = queryParams.get('show_archived') === 'true';
    const perPageFromUrlRaw = queryParams.get('perPage');
    const perPageFromUrl = perPageFromUrlRaw
      ? parseInt(perPageFromUrlRaw, 10)
      : perPage;

    return {
      organisationId: user?.organisation?.id,
      page: pageFromUrl,
      perPage: perPageFromUrl,
      query: queryFromUrl,
      isActive: !isArchived,
      orderBy,
      sortOrder: sortOrder as 'asc' | 'desc',
    };
  }, [location.search, orderBy, perPage, queryParams, sortOrder, user?.organisation?.id]);

  const isQueryReady =
    !isRestoring &&
    queryParams.has('orderBy') &&
    queryParams.has('sortOrder') &&
    (storedPerPage === 10 || queryParams.has('perPage'));

  const careHomesQuery = useCareHomesListQuery(careHomesQueryInput, isQueryReady);

  const careHomes: ICareHome[] = careHomesQuery.data?.items ?? [];
  const total = careHomesQuery.data?.total ?? 0;

  useEffect(() => {
    if (!careHomesQuery.isFetching) {
      setIsTableLoading(false);
    }
  }, [careHomesQuery.isFetching]);

  // Transform care homes data for the table
  const tableData: CareHomeTableItem[] = careHomes.map((careHome) => {
    // Safely check if address exists and has valid city
    const hasValidAddress = careHome.address && careHome.address.city;

    // Format created_at to DD.MM.YYYY
    let formattedDate = '-';
    if (careHome.created_at) {
      const date = new Date(careHome.created_at);
      formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}.${date.getFullYear()}`;
    }

    return {
      id: careHome.id,
      name: careHome.name,
      location: hasValidAddress ? careHome.address.city : '-',
      created_at: formattedDate,
    } as CareHomeTableItem;
  });

  // Define columns for the table
  const columns: ITableColumn<CareHomeTableItem>[] = [
    { key: 'name', label: t('nameOfCareHome'), sortable: true },
    {
      key: 'location',
      label: t('city'),
      sortable: true,
      render: (data) => <Box textAlign="left">{data.location}</Box>,
    },
    {
      key: 'created_at',
      label: t('activeSince'),
      sortable: true,
      render: (data) => <Box textAlign="left">{data.created_at}</Box>,
    },
  ];

  // Handle row click to navigate to care home details
  const handleRowClick = (careHome: CareHomeTableItem) => {
    navigate(`/care-home/${careHome.id}`);
  };

  const handlePageChange = (page: number) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', page.toString());
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  // Handler for perPage changes
  const handlePerPageChange = (newPerPage: number) => {
    updatePerPage(newPerPage); // First update localStorage
    setPerPage(newPerPage);
    setCurrentPage(1);
    
    const newParams = new URLSearchParams(location.search);
    newParams.set('perPage', newPerPage.toString());
    newParams.set('page', '1');
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Filter tags for Current and Archived
  const filterTags = [
    { label: t('general.current'), value: 'current' },
    { label: t('general.archived'), value: 'archived' },
  ];

  // Handle filter tag selection
  const handleFilterSelect = (value: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    
    if (value === 'archived') {
      newSearchParams.set('show_archived', 'true');
    } else {
      newSearchParams.delete('show_archived');
    }
    
    newSearchParams.set('page', '1'); // Reset to page 1 when changing filters
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  const buildPdfDocument = (exportDate: Date) => {
    const columns: ReportColumn[] = [
      { key: 'name', header: t('nameOfCareHome'), flex: 2, align: 'left' },
      { key: 'location', header: t('city'), flex: 2, align: 'left' },
      { key: 'created_at', header: t('activeSince'), flex: 1, align: 'left' },
    ];

    const rows: ReportRow[] = tableData.map((c) => ({
      name: c.name || '-',
      location: c.location || '-',
      created_at: c.created_at || '-',
    }));

    return (
      <A4ReportDocument
        title={t('facilities')}
        subtitle={t('totalItem', { count: total })}
        headerRightLeadingLogo={{ src: '/assets/images/logos/alea-cockpit-logo.png', height: 27 }}
        headerRightLabel={t('exclusively_with')}
        headerRightTrailing={<SeniSmartLogo width={73} height={24} />}
        filterLabel={selectedFilter === 'archived' ? t('general.archived') : t('general.current')}
        filterTagVariant={selectedFilter === 'archived' ? 'archived' : 'active'}
        filterValue=""
        columns={columns}
        rows={rows}
        organisationName={user?.organisation?.name}
        exportedAtLabel={t('general.export', { defaultValue: 'Export' })}
        exportedAtValue={formatExportTimestamp(exportDate)}
        pageLabel={t('general.page', { defaultValue: 'Page' })}
      />
    );
  };

  // Handle print functionality
  const handlePrint = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportDate = new Date();
      await printReactPdf({
        title: `facilities-${exportDate.toISOString().slice(0, 10)}`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (e) {
      console.error('Failed to print PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle export as PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    
    try {
      const exportDate = new Date();
      const exportDay = exportDate.toISOString().slice(0, 10);
      setIsExporting(true);

      await downloadReactPdf({
        filename: `facilities-report-${exportDay}.pdf`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCsv = () => {
    const exportDate = new Date();
    const exportDay = exportDate.toISOString().slice(0, 10);

    type CsvRow = CareHomeTableItem;
    const columns: CsvColumn<CsvRow>[] = [
      { header: t('nameOfCareHome'), value: (r) => r.name || '-' },
      { header: t('city'), value: (r) => r.location || '-' },
      { header: t('activeSince'), value: (r) => r.created_at || '-' },
    ];

    const rows = tableData;
    const csv = buildCsv(columns, rows);
    downloadCsv(`facilities-report-${exportDay}.csv`, csv);
  };

  // Dynamic download options
  const downloadOptions: DownloadOption[] = [
    {
      id: 'print',
      label: 'Print',
      translationKey: 'general.print',
      onClick: handlePrint
    },
    {
      id: 'pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: handleExportPdf
    },
    {
      id: 'csv',
      label: 'Export as CSV',
      translationKey: 'general.exportAsCsv',
      onClick: handleExportCsv
    }
  ];

  return (
    <Box data-testid="care-home-list-page">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        mb={{ base: '20px', md: '10px' }}
        h={{ base: 'auto', md: 75 }}
        flexDirection="row"
      >
        <Flex flexDirection="column">
          <Heading
            size="lg"
            fontSize={25}
            fontWeight={500}
            pl={{ base: 0, md: 0 }}
            mb={{ base: 1, md: 0 }}
          >
            {t('facilities')}
          </Heading>
          {careHomesQuery.isLoading || careHomesQuery.isFetching || isTableLoading ? (
            <Skeleton height="24px" width="120px" mt="4px" startColor="#c0c0c0" endColor="#e0e0e0" />
          ) : (
            <Text fontSize={16} color="gray.500" mt="4px">
              {t('totalItem', { count: total })}
            </Text>
          )}
        </Flex>
      </Flex>
      
      <Box mb="25px">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={{ base: 'column', md: isLargerThan1120 ? 'row' : 'column' }}
          gap={{ base: '16px' }}
          flexWrap={'wrap'}
        >
          <AleaFilterTags
            tags={filterTags}
            selectedTags={[selectedFilter]}
            onTagSelect={handleFilterSelect}
          />
          <Flex 
            gap="10px" 
            alignItems="center" 
            position="relative"
            alignSelf="flex-start"
            width={{ base: '100%', md: 'auto' }}
          >
            <Box w={{ base: '100%', md: '320px' }}>
              <SearchBar
                onClose={handleClearSearch}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isHardwarePage={true}
                height="50px"
                placeholderLanguageKey="facilities.searchPlaceholder"
              />
            </Box>
            <AleaDownloadButton options={downloadOptions} />
          </Flex>
        </Flex>
      </Box>

      <Box 
        bg="white" 
        borderRadius="20px" 
        overflow="auto" 
        minHeight={500}
        ref={tableContainerRef}
        className="care-homes-table-container"
      >
        <AleaTable<CareHomeTableItem>
          data={tableData}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={careHomesQuery.isLoading || careHomesQuery.isFetching || isTableLoading}
          total={total}
          noRecordsText={t('noDataYet')}
          usePagination={true}
          currentPage={currentPage}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      </Box>
    </Box>
  );
};

export default CareHomeListPage;
